import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';

const BusinessContext = createContext(null);

export const useBusinessContext = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusinessContext must be used within BusinessProvider');
  }
  return context;
};

// ============================================
// TIER CONFIGURATION - MASTER REFERENCE
// ============================================
// This is the single source of truth for feature tiers.
// Marketing pages should match this exactly.
// ============================================

const TIER_HIERARCHY = {
  'Solo': 1,
  'Pro': 2,
  'Enterprise': 3,
};

// Features and their minimum required tier
const FEATURE_TIERS = {
  // ============================================
  // SOLO FEATURES ($19/mo) - Available to all
  // ============================================
  // Core Business Management
  'customers': 'Solo',
  'jobs': 'Solo',
  'invoicing': 'Solo',
  'expenses': 'Solo',
  'leads': 'Solo',
  
  // Job Features (Solo)
  'job-list-view': 'Solo',
  'job-status-filters': 'Solo',
  'multi-service-jobs': 'Solo',
  'quote-calculator': 'Solo',
  'job-map-view': 'Solo',           // Can VIEW jobs on map, but no optimization
  'manual-quote-emails': 'Solo',    // Can manually send quotes
  'manual-prep-emails': 'Solo',     // Can manually send prep emails
  
  // Reports (Solo) - Tax focused for new operators
  'schedule-c': 'Solo',
  'tax-calculator': 'Solo',
  'quarterly-tax-estimates': 'Solo',
  'income-summary': 'Solo',
  'expense-summary': 'Solo',
  
  // Tools (Solo)
  'suddysnap': 'Solo',              // Receipt scanner
  'basic-emails': 'Solo',           // Manual email sending

  'invoice-payment-links': 'Solo',
  'online-payments': 'Solo',
  
  // ============================================
  // PRO FEATURES ($49/mo) - Team & Efficiency
  // ============================================
  // Team Management
  'team-members': 'Pro',            // Up to 15 team members
  
  // Route Optimization (SuddyRoute™)
  'route-optimization': 'Pro',      // Auto-optimize routes
  'gps-start-location': 'Pro',      // Start from current GPS
  'route-strategies': 'Pro',        // Revenue/Balanced/Distance comparison
  'navigate-all': 'Pro',            // One-tap navigation to all stops
  
  // Automated Communications
  'automated-emails': 'Pro',        // Auto-send reminders, follow-ups
  'payment-reminders': 'Pro',       // Overdue invoice reminders
  'review-requests': 'Pro',         // Post-job review requests
  'appointment-reminders': 'Pro',   // 24hr appointment reminders
  
  // Customer Features
  'customer-portal': 'Pro',         // Customer self-service portal
  
  // Reports (Pro)
  'pnl-reports': 'Pro',             // Profit & Loss statements
  'revenue-analytics': 'Pro',       // Revenue trends, comparisons

  // Pro features (portal is login + full features)
  'portal-job-history': 'Pro',
  'portal-quote-requests': 'Pro',
  
  // Integrations
  'quickbooks': 'Pro',              // QuickBooks sync
  
  // ============================================
  // ENTERPRISE FEATURES ($129/mo) - Scale & Customize
  // ============================================
  // Team
  'unlimited-team': 'Enterprise',   // Unlimited team members
  
  // Customization
  'custom-email-templates': 'Enterprise',  // Edit email templates
  'custom-branding': 'Enterprise',         // Logo in emails, portal
  
  // Marketing Automation
  're-engagement': 'Enterprise',    // 90/180/365 day re-engagement campaigns
  'seasonal-reminders': 'Enterprise', // Seasonal service reminders
  
  // Advanced Features
  'api-access': 'Enterprise',       // REST API access
  'advanced-analytics': 'Enterprise', // Custom reports, data exports
  'multi-location': 'Enterprise',   // Multiple business locations
};

// ============================================
// FEATURE DISPLAY INFO (for upgrade prompts)
// ============================================
const FEATURE_INFO = {
  'route-optimization': {
    name: 'Route Optimization',
    description: 'Automatically optimize your daily routes to save time and fuel',
    icon: '🗺️',
    tier: 'Pro',
  },
  'gps-start-location': {
    name: 'GPS Start Location',
    description: 'Start route optimization from your current location',
    icon: '📍',
    tier: 'Pro',
  },
  'route-strategies': {
    name: 'Route Strategies',
    description: 'Compare Revenue, Balanced, and Distance optimization strategies',
    icon: '⚖️',
    tier: 'Pro',
  },
  'navigate-all': {
    name: 'Navigate All Stops',
    description: 'One-tap handoff to Google Maps or Apple Maps with all stops',
    icon: '🧭',
    tier: 'Pro',
  },
  'automated-emails': {
    name: 'Automated Emails',
    description: 'Automatically send reminders, follow-ups, and review requests',
    icon: '📧',
    tier: 'Pro',
  },
  'customer-portal': {
    name: 'Customer Portal',
    description: 'Let customers view jobs, pay invoices, and request quotes online',
    icon: '🌐',
    tier: 'Pro',
  },
  'pnl-reports': {
    name: 'P&L Reports',
    description: 'Track your true profit with detailed financial reports',
    icon: '📊',
    tier: 'Pro',
  },
  'custom-email-templates': {
    name: 'Custom Email Templates',
    description: 'Fully customize your email templates with your branding',
    icon: '✏️',
    tier: 'Enterprise',
  },
  'advanced-analytics': {
    name: 'Advanced Analytics',
    description: 'Deep insights, custom reports, and data exports',
    icon: '📈',
    tier: 'Enterprise',
  },
};

export const BusinessProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = authService.getToken();
    if (token && authService.isAuthenticated()) {
      const userData = authService.getUser();
      
      // Double-check trial status
      const trialStatus = authService.getTrialStatus();
      if (trialStatus && !trialStatus.hasValidAccess) {
        // Trial expired, logout
        authService.logout();
        setUser(null);
      } else {
        setUser(userData);
      }
    }
    setLoading(false);
  }, []);

  // ============================================
  // AUTHENTICATION
  // ============================================
  const login = async (email, password) => {
    const result = await authService.login(email, password);
    
    // Check if MFA is required - return early without setting state
    if (result.mfaRequired) {
      return {
        mfaRequired: true,
        mfaToken: result.mfaToken
      };
    }

    const { token, user: userData } = result;

    // Check trial status BEFORE setting user or storing in localStorage
    if (userData.subscriptionStatus === 'Trial' && !userData.hasValidAccess) {
      throw new Error('Trial period has ended. Please subscribe to continue.');
    }
    
    if (!userData.hasValidAccess) {
      throw new Error('Your subscription is not active. Please contact support.');
    }
    
    // Only store in localStorage if validation passes
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    return userData;
  };

  const completeMfaLogin = async (authResponse) => {
    // Build user data object matching your normal login structure
    const userData = {
      id: authResponse.userId,
      businessId: authResponse.businessId,
      email: authResponse.email,
      firstName: authResponse.firstName,
      lastName: authResponse.lastName,
      businessName: authResponse.businessName,
      role: authResponse.role || 'Owner',
      subscriptionStatus: authResponse.subscriptionStatus,
      subscriptionPlan: authResponse.subscriptionPlan,
      trialDaysRemaining: authResponse.trialDaysRemaining,
      isTrialActive: authResponse.subscriptionStatus === 'Trial',
      hasValidAccess: authResponse.hasValidAccess,
      isFoundingMember: authResponse.isFoundingMember === true,
      workflowMode: authResponse.workflowMode || 'Standard',
    };

    // Check access before completing login
    if (!authResponse.hasValidAccess) {
      if (authResponse.subscriptionStatus === 'Trial') {
        throw new Error('Trial period has ended. Please subscribe to continue.');
      }
      throw new Error('Your subscription is not active. Please contact support.');
    }
    
    // Store the token and user data
    localStorage.setItem('token', authResponse.token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Update state
    setUser(userData);
    
    return { ...authResponse, user: userData };
  };

  const register = async (userData) => {
    const { user } = await authService.register(userData);
    setUser(user);
    return user;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Merge fields into the cached user (e.g. after changing workflow mode in Settings)
  const updateUserFields = (patch) => {
    if (!user) return;
    const updated = { ...user, ...patch };
    localStorage.setItem('user', JSON.stringify(updated));
    setUser(updated);
  };

  // ============================================
  // SUBSCRIPTION & TIER HELPERS
  // ============================================
  
  // Get the current subscription plan (Solo, Pro, Enterprise)
  const subscriptionPlan = user?.subscriptionPlan || 'Solo';
  
  // Get subscription status (Trial, Active, Cancelled, etc.)
  const subscriptionStatus = user?.subscriptionStatus || 'Trial';
  
  // Is the user currently in a trial?
  const isTrialActive = user?.isTrialActive === true || user?.isTrialActive === 'true';
  
  // Days remaining in trial
  const trialDaysRemaining = parseInt(user?.trialDaysRemaining) || 0;
  
  // Is user on a paid subscription (not trial)?
  const hasActiveSubscription = subscriptionStatus === 'Active';
  
  // ============================================
  // FEATURE ACCESS HELPERS
  // ============================================
  
  /**
   * Check if user can access a feature
   * @param {string} featureKey - The feature key to check
   * @returns {boolean} - Whether user has access
   */
  const canAccessFeature = (featureKey) => {
    const requiredTier = FEATURE_TIERS[featureKey];
    if (!requiredTier) return true; // Unknown feature = allow access
    
    const userTierLevel = TIER_HIERARCHY[subscriptionPlan] || 1;
    const requiredTierLevel = TIER_HIERARCHY[requiredTier] || 1;
    
    // During trial, allow access to all features
    if (isTrialActive) return true;
    
    // Otherwise check tier level
    return userTierLevel >= requiredTierLevel;
  };
  
  /**
   * Get the required tier for a feature
   * @param {string} featureKey - The feature key
   * @returns {string} - The tier name (Solo, Pro, Enterprise)
   */
  const getFeatureTier = (featureKey) => {
    return FEATURE_TIERS[featureKey] || 'Solo';
  };
  
  /**
   * Check if a feature is above user's current tier (for showing upgrade prompts)
   * @param {string} featureKey - The feature key
   * @returns {boolean} - Whether feature requires upgrade
   */
  const requiresUpgrade = (featureKey) => {
    if (isTrialActive) return false; // During trial, nothing requires upgrade
    
    const requiredTier = FEATURE_TIERS[featureKey];
    if (!requiredTier) return false;
    
    const userTierLevel = TIER_HIERARCHY[subscriptionPlan] || 1;
    const requiredTierLevel = TIER_HIERARCHY[requiredTier] || 1;
    
    return userTierLevel < requiredTierLevel;
  };
  
  /**
   * Get feature info for upgrade prompts
   * @param {string} featureKey - The feature key
   * @returns {object|null} - Feature info or null
   */
  const getFeatureInfo = (featureKey) => {
    return FEATURE_INFO[featureKey] || null;
  };
  
  /**
   * Get tier badge info for displaying on features during trial
   * @param {string} featureKey - The feature key
   * @returns {object|null} - Badge info or null if Solo tier
   */
  const getTierBadge = (featureKey) => {
    const tier = FEATURE_TIERS[featureKey];
    if (!tier || tier === 'Solo') return null;
    
    if (tier === 'Pro') {
      return {
        label: 'PRO',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        tier: 'Pro',
      };
    }
    
    if (tier === 'Enterprise') {
      return {
        label: 'ENTERPRISE',
        color: 'bg-purple-100 text-purple-700 border-purple-200',
        tier: 'Enterprise',
      };
    }
    
    return null;
  };
  
  /**
   * Should we show tier badges? (Only during trial)
   */
  const shouldShowTierBadges = isTrialActive;

  // ============================================
  // BACKWARD COMPATIBILITY
  // ============================================
  const businessId = user?.businessId?.toString() || '';
  const businessName = user?.businessName || '';

  // Role-based helpers
  const userRole = user?.role || null;
  const isOwner = userRole === 'Owner';
  const isEmployee = userRole === 'Employee';
  const isApplicationAdmin = userRole === 'ApplicationAdmin';

  // Workflow mode is a business-level view preference: 'Simple' | 'Standard'
  const workflowMode = ['Simple', 'Standard'].includes(user?.workflowMode)
    ? user.workflowMode
    : 'Standard';
  const isSimpleMode = workflowMode === 'Simple';

  const value = {
    // Auth properties
    user,
    login,
    completeMfaLogin,
    register,
    logout,
    isAuthenticated: !!user,
    loading,
    
    // Backward compatibility
    businessId,
    businessName,

    // Role information
    userRole,
    isOwner,
    isEmployee,
    isApplicationAdmin,

    // Workflow mode
    workflowMode,
    isSimpleMode,
    updateUserFields,
    
    // Subscription information
    subscriptionPlan,
    subscriptionStatus,
    isTrialActive,
    trialDaysRemaining,
    hasActiveSubscription,
    isFoundingMember: user?.isFoundingMember === true,
    
    // Feature access helpers
    canAccessFeature,
    getFeatureTier,
    requiresUpgrade,
    getFeatureInfo,
    getTierBadge,
    shouldShowTierBadges,
    
    // Tier constants (for components that need them)
    TIER_HIERARCHY,
    FEATURE_TIERS,
    FEATURE_INFO,
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};